'use client';

import { AnimatePresence, motion } from 'motion/react';
import { GraphicLottie } from '@/app/components/questionnaireV2/components/survey-item/components/interstitial/interstitial-shared';

interface LoadingBarItem {
  id: string;
  label: string;
  duration: number;
  delay: number;
}

const loadingBars: LoadingBarItem[] = [
  {
    id: 'bar-1',
    label: 'Calculating your lifetime Risk Score',
    duration: 3,
    delay: 0,
  },
  {
    id: 'bar-2',
    label: 'Generating your personalized Action Plan',
    duration: 3,
    delay: 1,
  },
  {
    id: 'bar-3',
    label: 'Developing your annual Testing Plan',
    duration: 3,
    delay: 2,
  },
  {
    id: 'bar-4',
    label: 'Finalizing updates',
    duration: 3,
    delay: 3,
  },
];

const headerImage = 'risk_grid_small.json';

/**
 * Minimal version of CalculatingCancerRisk for health data recalculation.
 * Shows 4 loading bars with descriptive text and the animated risk grid header.
 */
export const RecalculatingCancerRisk = () => {

  return (
    <div className="dashboard-reset flex h-full w-full flex-col items-center justify-center overflow-hidden pt-[56px] md:pt-[72px]">
      {/* Animated background grid */}
      <div className="flex h-[100px] w-full max-w-[530px] justify-center overflow-hidden -mt-[125px] md:-mt-[150px]">
        <div className="flex -mt-[15px]">
          <GraphicLottie srcUrl={headerImage} loop={true} className="h-[115px] w-[177px] flex-shrink-0" />
          <GraphicLottie srcUrl={headerImage} loop={true} className="h-[115px] w-[177px] flex-shrink-0" />
          <GraphicLottie srcUrl={headerImage} loop={true} className="h-[115px] w-[177px] flex-shrink-0" />
        </div>
      </div>
      
      <div className="h-[20px]" />

      {/* Main content container */}
      <div className="flex w-full justify-center px-8">
        <div className="flex w-full max-w-[307px] flex-col items-center">
          {/* Header text */}
          <div className="mb-10 w-full text-center">
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              className="mb-2 text-[28px] font-bold leading-[1.2] text-white"
            >
              We are analyzing your results
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2, ease: 'easeInOut' }}
              className="text-base font-normal leading-[1.6] text-white/90"
            >
              Please do not close this browser window or press the back button
            </motion.p>
          </div>

          {/* Loading bars */}
          <div className="w-full max-w-[264px] space-y-[30px]">
            <AnimatePresence>
              {loadingBars.map((item) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.4,
                    delay: item.delay,
                    ease: 'easeOut',
                  }}
                  className="flex w-full flex-col gap-2"
                >
                  {/* Progress bar container */}
                  <div className="h-4 w-full overflow-hidden rounded-full bg-[#151a20]">
                    <motion.div
                      initial={{ width: '0%' }}
                      animate={{
                        width: '100%',
                      }}
                      transition={{
                        duration: item.duration,
                        delay: item.delay,
                        ease: 'easeOut',
                      }}
                      className="h-full rounded-full"
                      style={{
                        background:
                          'linear-gradient(90deg, #7CF1C7 0%, #FF9473 164.58%)',
                        willChange: 'width',
                      }}
                    />
                  </div>

                  {/* Label text */}
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      duration: 0.3,
                      delay: item.delay + 0.2,
                    }}
                    className="text-sm font-normal leading-[1.43] text-white"
                  >
                    {item.label}
                  </motion.p>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};
